
import React from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Image, FileAudio, FileVideo, File } from "lucide-react";
import { MediaCardProps } from '@/types/mediaLibrary';
import { formatFileSize, getFileExtension } from '@/utils/fileUtils';

export default function MediaCard({ file, onClick, onDelete }: MediaCardProps) {
  const isImage = file.type === 'image';
  const isAudio = file.type === 'audio';
  const isVideo = file.type === 'video';

  // Handle right-to-left language
  const isRTL = file.language_code === 'ar' || file.language_code === 'he';

  // Check if file is associated with exhibits
  const isLinkedToExhibit = file.exhibit_media && file.exhibit_media.length > 0;

  return (
    <Card className={`overflow-hidden group cursor-pointer bg-white text-gray-900 border-gray-200 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div onClick={onClick} className="relative">
        {/* Media preview */}
        <div className={`aspect-[4/3] relative ${isAudio || (!isImage && !isVideo) ? 'bg-gray-100 flex items-center justify-center' : ''}`}>
          {isImage ? (
            <img 
              src={file.url} 
              alt={file.filename} 
              className="w-full h-full object-cover"
            />
          ) : isVideo ? (
            <video 
              src={file.url}
              className="w-full h-full object-cover"
            />
          ) : isAudio ? (
            <FileAudio size={48} className="text-gray-500" />
          ) : (
            <File size={48} className="text-gray-500" />
          )}
          
          {/* Overlay for non-images with file extension */}
          {!isImage && (
            <div className="absolute bottom-2 right-2 bg-white/95 text-gray-800 px-2 py-1 rounded text-sm font-mono uppercase font-medium border border-gray-200">
              {getFileExtension(file.filename)}
            </div>
          )}
        </div>

        {/* Media info */}
        <div className="p-3 border-t border-gray-200">
          <p className="text-base font-medium truncate text-gray-900" title={file.filename}>
            {file.filename}
          </p>
          <div className="flex justify-between items-center mt-2">
            <p className="text-sm text-gray-600">
              {formatFileSize(file.size)}
            </p>
            {file.language_code && (
              <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                {file.language_code}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Delete button - only show if not linked to exhibits */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button 
          size="icon" 
          variant="destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isLinkedToExhibit}
          title={isLinkedToExhibit ? "Cannot delete file linked to exhibits" : "Delete file"}
          className="h-8 w-8 bg-red-600 hover:bg-red-700 text-white shadow-md"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}
